import { ANNOTATION_VALUE } from "@/constants/annotation-values"

/**
 * Brand status tiers for string-metric visualizations. One hue per state — good
 * (green), bad (red-orange), neutral (warm grey), medium (amber) — taken from the
 * @coval/ui data-viz ramps (seriesPalette / chart tokens), the same hues the rest
 * of the app uses for pass/fail. Tier [0] is the ramp's mid tone — the canonical
 * chart-fill color used for a single group; tiers [1]–[3] step through the ramp's
 * strong, dark, and light tones so that when multiple groups (agents/personas)
 * share the same values, each group takes a distinct tier while keeping its hue
 * meaning. Status colors are reserved: they are never reused as a categorical
 * series color (see CHART_COLORS in @/components/charts/seriesPalette).
 *
 * Tier order: [0] mid · [1] strong · [2] dark · [3] light.
 */
const GOOD = ["#61e6c2", "#129a75", "#06503c", "#c8f3e7"] as const // green (pass / good)
const BAD = ["#f6775e", "#bf2203", "#611000", "#f8cfc7"] as const // red-orange (fail / bad)
const NEUTRAL = ["#8a877b", "#615e54", "#3d3b34", "#c4c1b6"] as const // warm grey (unknown / neutral)
const MEDIUM = ["#f7cc6f", "#e29b03", "#724e00", "#f9ebcd"] as const // amber (medium)

/**
 * Predefined color schemes for common string metric patterns (default variant).
 */
export const COLOR_SCHEMES = {
  "yes-no-unknown": { YES: GOOD[0], NO: BAD[0], UNKNOWN: NEUTRAL[0] },
  // Inverted: NO is good, YES is bad (e.g., "Agent Repeats Itself")
  "no-yes-unknown": { YES: BAD[0], NO: GOOD[0], UNKNOWN: NEUTRAL[0] },
  "pass-fail": { PASS: GOOD[0], FAIL: BAD[0] },
  "pass-fail-skip": { PASS: GOOD[0], FAIL: BAD[0], SKIP: NEUTRAL[0] },
  "high-medium-low": { HIGH: BAD[0], MEDIUM: MEDIUM[0], LOW: GOOD[0] },
  "good-bad-neutral": { GOOD: GOOD[0], BAD: BAD[0], NEUTRAL: NEUTRAL[0] },
  "true-false": { TRUE: GOOD[0], FALSE: BAD[0] },
} as const

export type ColorSchemeName = keyof typeof COLOR_SCHEMES

/**
 * Color scheme variants for multi-group visualizations. Each variant walks the
 * status tiers in lockstep, so groups sharing the same values stay distinct by
 * lightness while good/bad/neutral keep their hue meaning.
 */
export const COLOR_SCHEME_VARIANTS = {
  "yes-no-unknown": [
    { YES: GOOD[0], NO: BAD[0], UNKNOWN: NEUTRAL[0] },
    { YES: GOOD[1], NO: BAD[1], UNKNOWN: NEUTRAL[1] },
    { YES: GOOD[2], NO: BAD[2], UNKNOWN: NEUTRAL[2] },
    { YES: GOOD[3], NO: BAD[3], UNKNOWN: NEUTRAL[3] },
  ],
  // Inverted: NO is good, YES is bad (e.g., "Agent Repeats Itself")
  "no-yes-unknown": [
    { YES: BAD[0], NO: GOOD[0], UNKNOWN: NEUTRAL[0] },
    { YES: BAD[1], NO: GOOD[1], UNKNOWN: NEUTRAL[1] },
    { YES: BAD[2], NO: GOOD[2], UNKNOWN: NEUTRAL[2] },
    { YES: BAD[3], NO: GOOD[3], UNKNOWN: NEUTRAL[3] },
  ],
  "pass-fail": [
    { PASS: GOOD[0], FAIL: BAD[0] },
    { PASS: GOOD[1], FAIL: BAD[1] },
    { PASS: GOOD[2], FAIL: BAD[2] },
    { PASS: GOOD[3], FAIL: BAD[3] },
  ],
  "pass-fail-skip": [
    { PASS: GOOD[0], FAIL: BAD[0], SKIP: NEUTRAL[0] },
    { PASS: GOOD[1], FAIL: BAD[1], SKIP: NEUTRAL[1] },
    { PASS: GOOD[2], FAIL: BAD[2], SKIP: NEUTRAL[2] },
    { PASS: GOOD[3], FAIL: BAD[3], SKIP: NEUTRAL[3] },
  ],
  "high-medium-low": [
    { HIGH: BAD[0], MEDIUM: MEDIUM[0], LOW: GOOD[0] },
    { HIGH: BAD[1], MEDIUM: MEDIUM[1], LOW: GOOD[1] },
    { HIGH: BAD[2], MEDIUM: MEDIUM[2], LOW: GOOD[2] },
    { HIGH: BAD[3], MEDIUM: MEDIUM[3], LOW: GOOD[3] },
  ],
  "good-bad-neutral": [
    { GOOD: GOOD[0], BAD: BAD[0], NEUTRAL: NEUTRAL[0] },
    { GOOD: GOOD[1], BAD: BAD[1], NEUTRAL: NEUTRAL[1] },
    { GOOD: GOOD[2], BAD: BAD[2], NEUTRAL: NEUTRAL[2] },
    { GOOD: GOOD[3], BAD: BAD[3], NEUTRAL: NEUTRAL[3] },
  ],
  "true-false": [
    { TRUE: GOOD[0], FALSE: BAD[0] },
    { TRUE: GOOD[1], FALSE: BAD[1] },
    { TRUE: GOOD[2], FALSE: BAD[2] },
    { TRUE: GOOD[3], FALSE: BAD[3] },
  ],
} as const

/**
 * Canonical ordering for string values in stacked visualizations.
 * Ensures consistent vertical ordering across all time buckets.
 *
 * Values not in this map will be sorted alphabetically after canonical values.
 */
export const CANONICAL_STRING_ORDER: Record<string, number> = {
  // YES/NO patterns (positive -> negative -> neutral)
  YES: 0,
  NO: 1,
  UNKNOWN: 2,

  // PASS/FAIL patterns
  PASS: 0,
  FAIL: 1,
  SKIP: 2,

  // TRUE/FALSE patterns
  TRUE: 0,
  FALSE: 1,

  // GOOD/BAD patterns
  GOOD: 0,
  BAD: 1,
  NEUTRAL: 2,

  // HIGH/MEDIUM/LOW patterns
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,

  // Common variants (case-insensitive support)
  Yes: 0,
  No: 1,
  Unknown: 2,
  Pass: 0,
  Fail: 1,
  Skip: 2,
  True: 0,
  False: 1,
  Good: 0,
  Bad: 1,
  High: 0,
  Medium: 1,
  Low: 2,
}

export function getYesNoUnknownOrder(
  targetValues?: string[],
): Record<string, number> {
  const hasNo = targetValues?.includes(ANNOTATION_VALUE.NO) ?? false
  if (hasNo) {
    return { NO: 0, YES: 1, UNKNOWN: 2, No: 0, Yes: 1, Unknown: 2 }
  }
  return { YES: 0, NO: 1, UNKNOWN: 2, Yes: 0, No: 1, Unknown: 2 }
}
